import { Link } from '@remix-run/react'
import clsx from 'clsx'
import { useMemo } from 'react'
import {
  CreatePdfDialog,
  ListingBrokerNotes,
  ListingBrokers,
  ListingDescription,
  ListingFiles,
  ListingGallery,
  ListingLinks,
  ListingMeta,
  ListingSpaces,
  ListingStaticMap,
  ListingTransactions,
} from '~/components'
import { Badge, Button } from '~/components/ui'
import { getListingTypeLabel, Listing, Organization, User } from '~/models'
import { fmtAddress, getPropertyTypeClassName, STATUSES } from '~/utils'
import ListingAddToReport from './AddToReport'
import BrokerContact from './BrokerContact'
import { ConnectionsSection } from './external'
import ParcelDataDialog from './ParcelDataDialog'
import ListingPublicShare from './Share'

interface DetailProps {
  user: User | null
  listing: Listing
  organization: Organization
  canEdit: boolean
  location: any
  showBrokers: boolean
}

const Detail = ({
  user,
  listing,
  organization,
  canEdit,
  location,
  showBrokers,
}: DetailProps) => {
  const showAttachments = useMemo(() => {
    return (
      location.search.includes('attachments=true') ||
      !location.search.includes('report')
    )
  }, [location])

  const visibleLeaseSpaces = useMemo(() => {
    return (listing.leaseSpaces || []).filter((space) => {
      if (!user || listing.status !== 'leased') {
        return space.available === true
      }

      if (listing.transactions) {
        return (
          listing.transactions.findIndex(
            (t) => t.lease?.space._id === space._id
          ) === -1
        )
      }
      return space
    })
  }, [listing, user])

  return (
    <main className="px-4 py-8">
      <div className="mx-auto max-w-6xl">
        {user && (
          <>
            <div className="mb-4 flex justify-end gap-4">
              {canEdit && (
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="print:hidden">
                  <Link to={`/admin/listings/${listing.slug}/edit`}>Edit</Link>
                </Button>
              )}
              <CreatePdfDialog
                listingIds={[listing._id]}
                initialTitle={listing.name}
                singleListing={true}>
                <Button size="sm" variant="outline" className="print:hidden">
                  Download PDF
                </Button>
              </CreatePdfDialog>
              <ListingAddToReport listing={listing} />
            </div>
          </>
        )}

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex items-center justify-between gap-8 sm:items-start">
            <Badge className="text-base hover:bg-primary sm:mt-1">
              {STATUSES[listing.status]}
            </Badge>
            <ListingPublicShare className="sm:hidden" listing={listing} />
          </div>
          <h1 className="text-3xl font-medium text-foreground">
            {listing.name || listing.property.address}
          </h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="mb-4 mt-1 text-xl font-light text-muted-foreground">
            {fmtAddress(listing.property)}
          </p>

          <ListingPublicShare
            className="hidden sm:block print:hidden"
            listing={listing}
          />
        </div>

        <div className="flex flex-col-reverse gap-8 lg:flex-row">
          <div className="space-y-8 lg:w-[62%]">
            <div className="rounded-md border shadow-lg">
              <div className="break-inside-avoid overflow-hidden rounded-md">
                <ListingGallery listing={listing} />
              </div>
            </div>

            <div>
              <h4 className="mb-4 text-xl font-medium">Overview</h4>
              <ListingDescription listing={listing} />
            </div>

            {visibleLeaseSpaces.length > 0 && (
              <div className="break-inside-avoid">
                <h4 className="mb-4 text-xl font-medium">
                  {listing.status === 'leased'
                    ? 'Leased Spaces'
                    : 'Lease Spaces'}
                </h4>
                <ListingSpaces
                  listing={listing}
                  leaseSpaces={visibleLeaseSpaces}
                  className="max-h-none"
                  showAll={
                    visibleLeaseSpaces.length !==
                    (listing.leaseSpaces || []).filter((s) => s.available)
                      .length
                  }
                />
              </div>
            )}

            {(listing.transactions || []).length > 0 && (
              <div className="break-inside-avoid">
                <h4 className="mb-4 text-xl font-medium">Transacted Spaces</h4>
                <ListingTransactions listing={listing} />
              </div>
            )}

            {showAttachments && (
              <>
                {(listing.files || []).length > 0 && (
                  <div className="max-w-md break-inside-avoid">
                    <h4 className="mb-4  text-xl font-medium">Attachments</h4>
                    <ListingFiles listing={listing} />
                  </div>
                )}
                {(listing.links || []).length > 0 && (
                  <div className="max-w-md break-inside-avoid">
                    <h4 className="mb-4 text-xl font-medium">Links</h4>
                    <ListingLinks listing={listing} />
                  </div>
                )}
              </>
            )}

            {showBrokers && (
              <ListingBrokers
                className="block break-inside-avoid md:hidden"
                listing={listing}
              />
            )}

            {user && listing.meta.notes && (
              <div>
                <h4 className="mb-4 text-xl font-medium">Broker Notes</h4>
                <ListingBrokerNotes listing={listing} />
              </div>
            )}
          </div>
          <div className="space-y-8 lg:w-[38%]">
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <h2 className="mr-2 text-2xl font-medium">
                  {getListingTypeLabel(listing)}
                </h2>
                {listing.propertyTypes.map((value, index) => {
                  return (
                    <Badge
                      key={index}
                      className={clsx(
                        'text-base',
                        getPropertyTypeClassName(value)
                      )}>
                      <span>{value}</span>
                    </Badge>
                  )
                })}
              </div>
              {(listing.propertySubtypes || []).length > 0 && (
                <p className="mb-2 italic">
                  Secondary Uses: {listing.propertySubtypes!.join(', ')}
                </p>
              )}
              <ListingMeta className="text-lg" listing={listing} />
              {user &&
                listing.property.parcelApn &&
                organization.options.parcelDataEnabled && (
                  <div className="mt-2 flex justify-end">
                    <ParcelDataDialog listing={listing} />
                  </div>
                )}
              <ConnectionsSection listing={listing} />
            </div>
            {showBrokers && (
              <ListingBrokers className="hidden md:block" listing={listing}>
                {!organization!.options.appraisersOnly && (
                  <BrokerContact listing={listing} />
                )}
              </ListingBrokers>
            )}
          </div>
        </div>
      </div>
      {organization.options.placerEnabled && (
        <div className="mx-auto mt-6 max-w-6xl py-2">
          <h4 className="mb-4 text-xl font-medium">Placer Mobility Data</h4>
          {listing.property.postalCode && (
            <iframe
              title="Placer.ai Zip Code Map"
              src={`https://embedded-widget.placer.ai/widgets/v1/zip-code-map/?zipCode=${
                listing.property.postalCode
              }&address=${fmtAddress(listing.property)}`}
              loading="lazy"
              allowFullScreen={true}
              allow="clipboard-write"
              referrerPolicy="origin"
              scrolling="no"
              className="mb-8"
              style={{
                border: '1px solid rgb(229, 229, 229)',
                borderRadius: '8px',
                width: '100%',
                height: '600px',
              }}
            />
          )}
        </div>
      )}
      <div className="mx-auto mt-4 h-96 w-full max-w-6xl break-inside-avoid pb-6 md:mt-8 md:pb-16">
        <ListingStaticMap listing={listing} />
      </div>
    </main>
  )
}

export default Detail
